import { Box, Button, IconButton, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import type { JSX } from "react";

import { BulkCheckbox } from "../../../../components/bulk-selection/index.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import type { SupportPriority } from "../../types.js";
import { PRIORITY_GRID } from "./grids.js";

/** One editable priority row: select checkbox, live badge, name field, colour
 * picker, Save (dirty-gated), Delete. Extracted from `SettingsWorkflowsPanel`. */
export function PriorityRow({
  priority,
  selected,
  onToggle,
  onSave,
  onDelete,
  isPending,
}: {
  priority: SupportPriority;
  selected: boolean;
  onToggle: (checked: boolean, opts: { shiftKey: boolean }) => void;
  onSave: (body: Partial<SupportPriority>) => void;
  onDelete: () => void;
  isPending: boolean;
}): JSX.Element {
  const [name, setName] = useState(priority.name);
  const [color, setColor] = useState(priority.color ?? "#94a3b8");
  const dirty = name !== priority.name || color !== (priority.color ?? "#94a3b8");
  return (
    <Box
      component="li"
      sx={[
        { display: "grid", gridTemplateColumns: PRIORITY_GRID, alignItems: "center", gap: 1.5, borderRadius: 1, border: 1, p: 1, transition: "background-color 150ms" },
        selected
          ? { borderColor: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.1) }
          : { borderColor: "divider", bgcolor: "surfaceSoft" },
      ]}
    >
      <BulkCheckbox
        checked={selected}
        onChange={onToggle}
        ariaLabel={`Select priority ${priority.name}`}
      />
      <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <StatusBadge status={priority} />
      </Box>
      <TextField
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ minWidth: 0 }}
      />
      <Box
        component="input"
        type="color"
        value={color}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setColor(e.target.value)}
        title="Priority colour"
        sx={{ height: 32, width: 48, cursor: "pointer", borderRadius: 0.5, border: 1, borderColor: "divider", bgcolor: "background.default" }}
      />
      <Button
        variant="contained"
        size="small"
        disabled={!dirty || isPending}
        onClick={() => onSave({ name, color })}
        sx={{ minWidth: 0, px: 1.5, py: 0.5, fontSize: 12 }}
      >
        Save
      </Button>
      <IconButton
        size="small"
        onClick={onDelete}
        title="Delete priority"
        aria-label={`Delete priority ${priority.name}`}
        sx={{
          color: "text.disabled",
          "&:hover": { bgcolor: (t) => alpha(t.palette.error.main, 0.12), color: "error.main" },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
